<script setup lang="ts">
import { t } from '~/i18n';

const email = ref('');
const password = ref('');
const error = ref('');
const isLoading = ref(false);
const showPassword = ref(false);
const { signIn, user } = useAuth();

definePageMeta({
  layout: 'auth',
});

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

// SEO: 登录页面 noindex（搜索引擎不索引），但 OG 分享卡片仍受 i18n 影响。
useHead({
  title: () => t('landingPages.auth.login.seoDocumentTitle'),
  meta: [
    { charset: 'utf-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { name: 'robots', content: 'noindex, nofollow' },
    { name: 'title', content: () => t('landingPages.auth.login.seoTitle') },
    { name: 'description', content: () => t('landingPages.auth.login.seoDescription') },
  ],
});

const handleSubmit = async () => {
  error.value = '';
  isLoading.value = true;

  try {
    if (!email.value || !password.value) {
      error.value = t('landingPages.auth.common.emailPasswordRequired');
      isLoading.value = false;
      return;
    }

    await signIn({ email: email.value, password: password.value });
    await navigateTo('/workspace/create');
  } catch (err: any) {
    error.value =
      err.data?.message || err.message || t('landingPages.auth.common.invalidCredentials');
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <!-- Main Content -->
  <main class="flex-1 flex flex-col justify-center items-center relative z-10 px-4 py-8">
    <!-- Login Form Container -->
    <div
      class="w-full max-w-[400px] mx-auto panel-gradient rounded-3xl px-6 pt-4 pb-6 shadow-lg backdrop-blur-[20px]"
    >
      <!-- eslint-disable-next-line vue/no-undef-components -->
      <AuthLoggedInView v-if="user" />

      <!-- Login Form -->
      <div v-else class="w-full">
        <div class="flex items-center justify-between mb-16 md:mb-24">
          <h1 class="text-3xl md:text-4xl font-semibold tracking-tight text-black/90">
            {{ $t('landingPages.auth.login.title') }}
          </h1>
          <NuxtLink to="/" class="inline-block transition-opacity duration-300">
            <img src="/veesoai-condense-dark.svg" alt="Veeso AI" class="h-8 w-auto" />
          </NuxtLink>
        </div>

        <div class="flex flex-col gap-4">
          <!-- Google Login Section -->
          <!-- eslint-disable-next-line vue/no-undef-components -->
          <AuthGoogleLoginButton />

          <!-- Divider -->
          <div class="relative flex items-center justify-center">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-black/10"></div>
            </div>
            <div class="relative px-4 text-sm font-normal text-black/40 bg-white rounded">
              {{ $t('landingPages.auth.common.or') }}
            </div>
          </div>

          <!-- Email Login Section -->
          <form @submit.prevent="handleSubmit" class="flex flex-col gap-3">
            <!-- Email Input -->
            <input
              id="email"
              v-model="email"
              type="email"
              class="w-full h-10 px-4 text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 outline-none placeholder:text-black/40"
              :placeholder="$t('landingPages.auth.common.email')"
              required
              autocomplete="email"
              @input="error = ''"
            />

            <!-- Password Input -->
            <div class="relative border border-black/10 rounded-lg">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                class="w-full h-10 px-4 text-base font-normal text-black/90 bg-transparent transition-all duration-200 outline-none placeholder:text-black/40"
                :placeholder="$t('landingPages.auth.common.password')"
                required
                autocomplete="current-password"
                @input="error = ''"
              />
              <button
                type="button"
                @click="togglePasswordVisibility"
                class="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-black/40 hover:text-black/60 transition-colors duration-200"
                :aria-label="$t('landingPages.auth.common.togglePasswordVisibility')"
              ></button>
            </div>

            <!-- Submit Button -->
            <button
              type="submit"
              class="w-full h-10 px-4 flex items-center justify-center text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 tracking-[0.02em] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
              :disabled="isLoading"
            >
              {{
                isLoading
                  ? $t('landingPages.auth.login.submitting')
                  : $t('landingPages.auth.login.submit')
              }}
            </button>

            <!-- Error Message -->
            <div
              v-if="error"
              class="px-4 py-2.5 text-sm font-normal text-[#f90000] bg-[#f90000]/5 rounded-lg"
            >
              {{ error }}
            </div>

            <!-- Forgot Password Link -->
            <div class="flex justify-start">
              <NuxtLink
                to="/forgot"
                class="text-sm font-normal text-black/80 hover:text-black transition-colors duration-200"
              >
                {{ $t('landingPages.auth.common.forgotPassword') }}
              </NuxtLink>
            </div>

            <!-- Sign Up Link -->
            <div class="text-left text-sm font-normal text-black/80 mt-2">
              {{ $t('landingPages.auth.common.noAccount') }}
              <NuxtLink
                to="/signup"
                class="text-black/80 underline ml-1 transition-colors hover:text-black"
              >
                {{ $t('landingPages.auth.common.createAccount') }}
              </NuxtLink>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Terms Agreement Text -->
    <div class="mt-6 text-center text-xs font-normal text-white/50 px-4">
      {{ $t('landingPages.auth.common.termsPrefix') }}
      <NuxtLink to="/terms" class="text-white/50 underline transition-colors hover:text-white">
        {{ $t('landingPages.auth.common.termsOfService') }}
      </NuxtLink>
      {{ $t('landingPages.auth.common.and') }}
      <NuxtLink to="/privacy" class="text-white/50 underline transition-colors hover:text-white">
        {{ $t('landingPages.auth.common.privacyPolicy') }}
      </NuxtLink>
      .
    </div>
  </main>
</template>

<style scoped>
.panel-gradient {
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.6) 0%,
    rgba(255, 255, 255, 1) 20%,
    rgba(255, 255, 255, 1) 100%
  );
}

/* Prevent zoom on iOS */
@media (max-width: 640px) {
  input {
    font-size: 16px;
  }
}

/* Disable browser autofill styles */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  box-shadow: 0 0 0 30px transparent inset !important;
  transition: background-color 5000s ease-in-out 0s;
}

/* Autofill text color */
input:-webkit-autofill {
  -webkit-text-fill-color: rgba(0, 0, 0, 0.9) !important;
}
</style>
